'use client'
import { reviewApi } from "@/apis/review"
import { AuthenticationLink, Permissions } from "@/components/AuthenticationLink"
import { useUserStore } from "@/store/useUserStore"
import { useQuery } from "@tanstack/react-query"
import { ME_KEY } from "../../review/_components/useReview"
import { usePathname } from '@/locales/routing'
import { Circles } from "@/ui/icons/Circles"
import { FillHome } from "@/ui/icons/FillHome"
import { FillMessage } from "@/ui/icons/FillMessage"
import { FillTrophy } from "@/ui/icons/FillTrophy"
import { FillUser } from "@/ui/icons/FillUser"
import { FillWallet } from "@/ui/icons/FillWallet"
import { OutlineHome } from "@/ui/icons/OutlineHome"
import { OutlineMessage } from "@/ui/icons/OutlineMessage"
import { OutlineTrophy } from "@/ui/icons/OutlineTrophy"
import { OutlineUser } from "@/ui/icons/OutlineUser"
import { OutlineWallet } from "@/ui/icons/OutlineWallet"
import { Button, cn } from "@heroui/react"
import { motion } from "framer-motion"
import { useTranslations } from 'next-intl'
import { DREAMBOX_URL } from "@/util/dreambox"
import { buttonVariants, textVariants } from "./SideBar"



export const Navigator = (props: { className?: string, aside?: string, prefetch?: boolean }) => {
  const { className, aside, prefetch } = props
  const pathname = usePathname()

  const t = useTranslations()
  const isLogined = useUserStore((s: any) => s.isLogined)
  // 审核员才显示审核入口；与审核平台共用 me 缓存，普通用户每次会话只查一次
  const { data: reviewMe } = useQuery({ queryKey: ME_KEY, queryFn: reviewApi.me, enabled: !!isLogined, staleTime: 10 * 60_000, retry: false })
  const reviewer = reviewMe?.reviewer
  const isReviewer = !!reviewer && !reviewer.disabled && reviewer.stage >= 1

  const navList = [
    {
      icon: <OutlineHome className="flex-shrink-0" />,
      activeIcon: <FillHome className="flex-shrink-0" />,
      href: '/', desc: t('tab.home'),
      permission: Permissions.tourist
    },
    {
      icon: <OutlineMessage className="flex-shrink-0" />,
      activeIcon: <FillMessage className="flex-shrink-0" />,
      href: '/chats', desc: t('tab.chat'),
      permission: Permissions.user
    },
    {
      icon: <Circles className="flex-shrink-0" />,
      activeIcon: <Circles className="flex-shrink-0" />,
      href: '/circles', desc: t('tab.circles'),
      permission: Permissions.tourist
    },
    {
      icon: <OutlineUser className="flex-shrink-0" />,
      activeIcon: <FillUser className="flex-shrink-0" />,
      href: '/profile', desc: t('tab.profile'),
      permission: Permissions.user
    },
    // {
    //   icon: <OutlineWallet className="flex-shrink-0" />,
    //   activeIcon: <FillWallet className="flex-shrink-0" />,
    //   href: '/pricing', desc: t('tab.pricing'),
    //   permission: Permissions.user
    // },
    {
      icon: <OutlineTrophy className="flex-shrink-0" />,
      activeIcon: <FillTrophy className="flex-shrink-0" />,
      href: '/bonus', desc: t('tab.bonus'),
      permission: Permissions.user
    },
    // 梦箱（AI 跑团）是独立子站，未登录也能逛，登录由梦箱自己处理
    {
      icon: <DreamboxIcon className="flex-shrink-0" />,
      activeIcon: <DreamboxIcon className="flex-shrink-0" filled />,
      href: DREAMBOX_URL, desc: t('tab.dreambox'),
      permission: Permissions.tourist,
      beta: true,
    },
    ...(isReviewer ? [{
      icon: <ReviewShield className="flex-shrink-0" />,
      activeIcon: <ReviewShield className="flex-shrink-0" filled />,
      href: '/review', desc: t('review.brand'),
      permission: Permissions.user
    }] : []),
  ]


  return <nav className={className}>
    <ul className="flex flex-col gap-4 sm:gap-[20px]">
      {
        navList.map(item => {
          return <motion.li
            className="text-theme-primary overflow-hidden"
            key={item.desc}
            animate={aside}
            variants={buttonVariants} >

            <Button
              data-aside={aside}
              permission={item.permission}
              data-active={item.href === pathname}
              className={cn(
                "create-character-btn relative z-[1] min-w-[40px] justify-start pl-[24px] bg-transparent data-[hover=true]:bg-light-gray",
                "rounded-[12px] data-[aside=default]:rounded-full",
                "px-[8px] data-[aside=default]:pl-[24px] data-[aside=default]:pr-[16px]",
                "data-[active=true]:bg-seleced-bg data-[active=true]:font-bold"
              )}
              as={AuthenticationLink}
              href={item.href}
              prefetch={prefetch}
              style={{
                "--heroui-hover-opacity": "1",
              } as any}

              startContent={item.href === pathname ?
                item.activeIcon :
                item.icon
              }
              fullWidth>

              {"beta" in item ? (
                <motion.span className="flex h-[18px] min-w-0 items-start gap-1" variants={textVariants}>
                  <span className="line-clamp-1">{item.desc}</span>
                  <BetaTag className="-translate-y-1.5" />
                </motion.span>
              ) : (
                <motion.span
                  className="line-clamp-1 h-[18px]"
                  variants={textVariants}>{item.desc}</motion.span>
              )}
              {/* 侧栏收起时文字隐藏，beta 角标挂到图标右上角 */}
              {"beta" in item && aside === 'close' ? <BetaTag decorative className="absolute end-0 top-0 scale-[0.85]" /> : null}

            </Button>
          </motion.li>
        })
      }
    </ul>
  </nav>
}
const BetaTag = ({ className, decorative }: { className?: string, decorative?: boolean }) => (
  <span aria-hidden={decorative || undefined} className={cn(
    "pointer-events-none shrink-0 rounded-full bg-gradient-to-r from-[#8B5CF6] to-[#EC4899] px-1.5 py-[1px] text-[9px] font-bold uppercase leading-[12px] tracking-wide text-white",
    className
  )}>beta</span>
)

/** 梦箱入口图标：盒子 + 星光，与导航其它图标同尺寸、同描边 */
function DreamboxIcon({ filled, ...props }: { filled?: boolean; className?: string }) {
  return <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M4.5 10.5h15v8a2 2 0 0 1-2 2h-11a2 2 0 0 1-2-2v-8Z" fill={filled ? 'currentColor' : 'none'} />
    <path d="M3 7.5a1 1 0 0 1 1-1h16a1 1 0 0 1 1 1v3H3v-3Z" fill={filled ? 'currentColor' : 'none'} />
    <path d="M12 12.6c.3 1.5.8 2 2.3 2.3-1.5.3-2 .8-2.3 2.3-.3-1.5-.8-2-2.3-2.3 1.5-.3 2-.8 2.3-2.3Z" fill={filled ? 'rgb(var(--c-seleced-bg))' : 'currentColor'} stroke={filled ? 'rgb(var(--c-seleced-bg))' : 'currentColor'} strokeWidth="1.2" />
    <path d="M9 3.5 10 5M15 3.5 14 5M12 2.5V4.5" strokeWidth="1.6" />
  </svg>
}

/** 审核入口图标：与导航其它图标同尺寸、同描边 */
function ReviewShield({ filled, ...props }: { filled?: boolean; className?: string }) {
  return <svg {...props} width="24" height="24" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M12 2.5 4.5 5.5v6c0 4.8 3.2 8.4 7.5 9.8 4.3-1.4 7.5-5 7.5-9.8v-6L12 2.5Z" />
    <path d="m8.8 12 2.2 2.2 4.2-4.4" stroke={filled ? 'rgb(var(--c-seleced-bg))' : 'currentColor'} />
  </svg>
}
